/**
 * Centralized copy for every Chalkboard upgrade prompt in the app.
 *
 * One feature = one entry. Pages import the preset by key and pass it to
 * `<UpgradeGateCard {...presets.erase_and_redraw} onUpgrade={...} />`
 * so the copy is consistent everywhere the user hits a Chalkboard wall.
 *
 * Keep the copy short, second-person, and benefit-led. No SaaS-speak.
 */

export const UPGRADE_PRICE = "$11.99/month";

// The parents' personal upgrade — different tier ($7.99) — surfaced in
// any place the user is a guardian.
export const CLIPBOARD_PRICE = "$7.99/month";

export const UPGRADE_GATES = {
  family_dashboard: {
    // Parents flagship — the cross-team view of every kid every event.
    featureName: "Family Sports Dashboard",
    benefit: "Keep every child, every team, and every schedule organized in one place.",
    target: "The Clipboard+",
    price: CLIPBOARD_PRICE,
    tagline: "Never double-book your kids again.",
  },
  erase_and_redraw: {
    featureName: "Erase and Redraw",
    benefit: "Bulk-edit your whole season — move every Tuesday practice, change every home game, reschedule a whole month at once.",
  },
  draw_it_up: {
    featureName: "Draw It Up",
    benefit: "Turn rough notes into clear team updates your families understand.",
  },
  volunteers: {
    featureName: "Volunteer Management",
    benefit: "Let families claim snack weeks themselves.",
  },
  read_receipts: {
    featureName: "Read receipts",
    benefit: "See exactly who hasn't seen your message.",
  },
  second_pin: {
    featureName: "More pinned posts",
    benefit: "Pin your rules, schedule, and snack list.",
  },
  multiple_guardians: {
    featureName: "Multiple Guardians",
    benefit: "Keep both households in the loop with one shared team view.",
  },
  unlimited_roster: {
    featureName: "Unlimited Roster",
    benefit: "The Chalkboard gives you room for your full squad, plus advanced roster tools.",
  },
  full_history: {
    featureName: "Full Chalk Talk History",
    benefit: "Keep your entire season of team communication in one place.",
  },
  audience_targeting: {
    featureName: "Audience Targeting",
    benefit: "Send updates to the right families without creating side chats.",
  },
  recurring_events: {
    featureName: "Recurring practices",
    benefit: "Practice every Tuesday and Thursday? Set it once with Chalkboard.",
  },
  league_os: {
    // Sales-led tier — uses a different CTA but rendered through the same card
    // for visual consistency.
    featureName: "League OS",
    benefit: "Run all your teams under one roof. Multi-team admin, league-wide broadcasts, and centralized billing.",
    target: "League OS",
    ctaLabel: "Talk to us",
  },
};

/**
 * Lookup helper for API error mapping. Backend `tier_insufficient` errors
 * include a `feature` string in the meta — we use that to find the right
 * preset and surface the same card instead of a raw error.
 */
const FEATURE_TO_GATE = {
  "Family Sports Dashboard": "family_dashboard",
  "Erase and Redraw": "erase_and_redraw",
  "Draw It Up": "draw_it_up",
  "AI play draft": "draw_it_up",
  "Volunteer Management": "volunteers",
  "Volunteer management": "volunteers",
  "More than 1 pinned posts": "second_pin",
  "Read receipts": "read_receipts",
  "Multiple guardians per athlete": "multiple_guardians",
  "Roster size beyond 15": "unlimited_roster",
  "Roster export": "unlimited_roster",
  "Recurring events": "recurring_events",
  "Audience targeting": "audience_targeting",
  "Full Chalk Talk history": "full_history",
  "League OS": "league_os",
};

export function gateForFeature(featureLabel) {
  const key = FEATURE_TO_GATE[featureLabel];
  return key ? UPGRADE_GATES[key] : null;
}
